"use client";

/**
 * @typedef {import("@prismicio/client").Content.ProductInformationSlice} ProductInformationSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<ProductInformationSlice>} ProductInformationProps
 * @type {import("react").FC<ProductInformationProps>}
 */
import { useEffect, useRef, useState } from "react";
import { isFilled } from "@prismicio/client";
import { PrismicRichText } from "@prismicio/react";
import Vague from "@/components/Vague";

const ProductInformation = ({ slice, context }) => {
  
  const { type } = context;
  console.log("type", type);
  const textRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (isExpanded) return;

      const element = textRef.current;
      if (!element) return;

      setIsOverflowing(element.scrollHeight > element.clientHeight + 1);
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);

    return () => {
      window.removeEventListener("resize", checkOverflow);
    };
  }, [slice.primary.text, isExpanded]);

  useEffect(() => {
    if (!isOverflowing && isExpanded) {
      setIsExpanded(false);
    }
  }, [isOverflowing, isExpanded]);

  useEffect(() => {
    setIsExpanded(false);
  }, [slice.primary.text]);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`contre-uno text-bleu-950 ${type === "property_rental" ? "bg-terracota-100" : "bg-bleu-50"}`}
    >
      <div className={`padding-y ${type === "property_rental" ? "bg-terracota-300" : "bg-bleu-100"} rounded-b-[32px] md:rounded-b-[40px]`}>
        <div className="padding-x framing">
          {isFilled.richText(slice.primary.text) && (
            <div className={`${type === "property_rental" ? "bg-terracota-100" : "bg-bleu-50"} rounded-2xl p-8 relative`}>
              <div className="hidden lg:block absolute -top-10 -right-3">
                <Vague />
              </div>
              <div
                ref={textRef}
                className={!isExpanded ? "line-clamp-[20] md:line-clamp-10" : "line-clamp-none"}
              >
                <PrismicRichText
                  field={slice.primary.text}
                  components={{
                    heading2: ({ children }) => <h2 className="h4 mb-6">{children}</h2>,
                    heading3: ({ children }) => <h3 className="h5 mb-2">{children}</h3>,
                    paragraph: ({ children }) => <p className="mb-3 body-s">{children}</p>,
                    list: ({ children }) => <ul className="body-s list-inside list-disc my-3">{children}</ul>,
                    oList: ({ children }) => <ol className="body-s list-inside list-decimal my-3">{children}</ol>,
                    listItem: ({ children }) => <li className="mb-1 body-s">{children}</li>,
                    oListItem: ({ children }) => <li className="mb-1 body-s">{children}</li>,
                    strong: ({ children }) => <strong>{children}</strong>,
                    em: ({ children }) => <em>{children}</em>,
                    hyperlink: ({ node, children }) => (
                      <a
                        href={node.data.url || "#"}
                        target={node.data.target || undefined}
                        rel={node.data.target === "_blank" ? "noopener noreferrer" : undefined}
                        className="underline"
                      >
                        {children}
                      </a>
                    ),
                  }}
                />
              </div>

              {isOverflowing && (
                <div className="flex justify-center">
                  <button type="button" className="cursor-pointer body-s pt-6 underline text-bleu-900" onClick={() => setIsExpanded((prev) => !prev)}>
                    {isExpanded ? "Voir moins" : "Voir plus"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ProductInformation;
